import type { PaginationModel } from '@montugroup/design-system';
import type { QueryObserverResult } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { keyBy } from 'lodash';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import useTableCountWithLoading from '@/hooks/table/useTableCountWithLoading';
import { useTableManualPagination } from '@/hooks/table/useTableManualPagination';
import useTableSortingOrder from '@/hooks/table/useTableSortingOrder';
import useGetPharmacistBatchDispense from '@/hooks/useGetPharmacistBatchDispense';
import useGetPharmacistOrders from '@/hooks/useGetPharmacistOrders';
import { getReasonStatusList, processDispensing } from '@/services/pharmacist.service';
import type {
  OrderStatus,
  PharmacistBatchOrder,
  PharmacistOrder,
  PharmacistReasonStatus,
  ProcessDispenseResult,
} from '@montugroup/circuit-shared/types/index';
import { noOp } from '@montugroup/circuit-shared/utils/noOp';

const DEFAULT_PAGE_SIZE = 50;
const INITIAL_PAGE = 0;

// eslint-disable-next-line no-shadow
export enum PharmacistOrderTableView {
  OPEN_ORDERS = 'openOrderOverview',
  PROCESSING_DISPENSE = 'processingDispense',
  DISPENSED_ORDERS = 'dispensedOverview',
  ALL_ORDERS = 'allOrderOverview',
  PENDING_COLLECTION = 'pendingCollection',
}

export type PharmacistOrdersData = {
  activeView: PharmacistOrderTableView;
  setActiveView: (activeView: PharmacistOrderTableView) => void;
  page: number;
  pageSize: number;
  handlePaginationModelChange: (model: PaginationModel) => void;
  handleSortingOrderChange: (sortOrder: SortingState) => void;
  search: string;
  setSearch: (search: string) => void;
  selectedOrders: number[];
  setSelectedOrders: (selectedOrders: number[]) => void;
  orders: PharmacistOrder[];
  batchOrders: PharmacistBatchOrder[];
  count: number;
  loading: boolean;
  unableToDispenseReasons: PharmacistReasonStatus[];
  setProcessingDispense: (loading: boolean) => void;
  refetchOrders: () => Promise<QueryObserverResult | undefined>;
  batchCount: number;
  statusFilter: OrderStatus[];
  setStatusFilter: (statusFilter: OrderStatus[]) => void;
  shouldResetPageIndex?: boolean;
  setShouldResetPageIndex: (value: boolean) => void;
  batchView: boolean;
  setBatchView: (batchView: boolean) => void;
  startDate: Date | null;
  setStartDate: (date: Date | null) => void;
  endDate: Date | null;
  setEndDate: (date: Date | null) => void;
};

export const PharmacistOrdersContext = createContext<PharmacistOrdersData>({
  activeView: PharmacistOrderTableView.ALL_ORDERS,
  setActiveView: noOp,
  page: INITIAL_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
  handlePaginationModelChange: noOp,
  handleSortingOrderChange: noOp,
  search: '',
  setSearch: noOp,
  selectedOrders: [],
  setSelectedOrders: noOp,
  orders: [],
  count: 0,
  loading: false,
  unableToDispenseReasons: [],
  setProcessingDispense: noOp,
  statusFilter: [],
  setStatusFilter: noOp,
  refetchOrders: () => Promise.resolve(undefined),
  batchOrders: [],
  batchCount: 0,
  shouldResetPageIndex: false,
  batchView: false,
  setBatchView: noOp,
  setShouldResetPageIndex: noOp,
  startDate: null,
  setStartDate: noOp,
  endDate: null,
  setEndDate: noOp,
});

const sortFieldOverrides = {
  patient_name: 'Patient.PatientUser.first_name',
  gp_name: 'GeneralPractitioner.Doctor.first_name',
} as const;

export function PharmacistOrdersProvider(props: PropsWithChildren) {
  const { children } = props;
  const [activeView, setActiveView] = useState<PharmacistOrderTableView>(PharmacistOrderTableView.OPEN_ORDERS);
  const {
    paginationModel: { page, pageSize },
    setPaginationModel,
    shouldResetPageIndex,
    setShouldResetPageIndex,
  } = useTableManualPagination({
    page: INITIAL_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const { sortingOrder, handleSortingOrderChange } = useTableSortingOrder({ sortFieldOverrides });
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [statusFilter, setStatusFilter] = useState<OrderStatus[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [processingDispense, setProcessingDispense] = useState<boolean>(false);
  const [unableToDispenseReasons, setUnableToDispenseReasons] = useState<PharmacistReasonStatus[]>([]);
  const [batchView, setBatchView] = useState<boolean>(false);

  const shouldDisableQuery =
    [
      PharmacistOrderTableView.OPEN_ORDERS,
      PharmacistOrderTableView.PROCESSING_DISPENSE,
      PharmacistOrderTableView.PENDING_COLLECTION,
    ].includes(activeView) || batchView;

  const {
    data: orderData,
    refetch: refetchOrders,
    isLoading: isOrdersLoading,
  } = useGetPharmacistOrders({
    page,
    pageSize,
    sortingOrder,
    filter: search,
    activeTab: activeView,
    statusFilter,
    isEnabled: !shouldDisableQuery,
    startDate,
    endDate,
  });

  const orders = useMemo(() => orderData?.orders || [], [orderData]);

  const {
    data: batchData,
    refetch: refetchBatchOrders,
    isLoading: isBatchOrdersLoading,
  } = useGetPharmacistBatchDispense({ pageSize, page, isEnabled: batchView });

  const batchOrders = useMemo(() => batchData?.data || [], [batchData?.data]);

  const count = useTableCountWithLoading(isOrdersLoading, orderData?.count);

  const batchCount = useTableCountWithLoading(isBatchOrdersLoading, batchOrders?.length);

  const handlePaginationModelChange = useCallback(
    (model: PaginationModel) => {
      if (model.pageSize !== pageSize) {
        setSelectedOrders([]);
      }
      setPaginationModel(model);
      setShouldResetPageIndex(false);
    },
    [pageSize, setPaginationModel, setShouldResetPageIndex],
  );

  useEffect(() => {
    const fetchReasons = async () => {
      const reasons = await getReasonStatusList();
      const reasonsById = keyBy(reasons.data, 'id');
      const orderedReason = [9, 10, 5, 12, 1, 13, 14];
      setUnableToDispenseReasons(
        orderedReason.map((reasonId) => reasonsById[reasonId]).filter((value) => value !== undefined),
      );
    };
    fetchReasons();
  }, []);

  useEffect(() => {
    setShouldResetPageIndex(true);
  }, [search, setShouldResetPageIndex]);

  const value = useMemo(
    () => ({
      activeView,
      setActiveView,
      page,
      pageSize,
      orders,
      batchOrders,
      count,
      batchCount,
      loading: processingDispense || isOrdersLoading || isBatchOrdersLoading,
      search,
      setSearch,
      refetchOrders,
      refetchBatchOrders,
      statusFilter,
      setStatusFilter,
      selectedOrders,
      setSelectedOrders,
      setProcessingDispense,
      handlePaginationModelChange,
      handleSortingOrderChange,
      unableToDispenseReasons,
      shouldResetPageIndex,
      setShouldResetPageIndex,
      batchView,
      setBatchView,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
    }),
    [
      activeView,
      page,
      pageSize,
      orders,
      batchOrders,
      count,
      batchCount,
      processingDispense,
      isOrdersLoading,
      isBatchOrdersLoading,
      search,
      refetchOrders,
      refetchBatchOrders,
      statusFilter,
      selectedOrders,
      handlePaginationModelChange,
      handleSortingOrderChange,
      unableToDispenseReasons,
      shouldResetPageIndex,
      setShouldResetPageIndex,
      batchView,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
    ],
  );

  return <PharmacistOrdersContext.Provider value={value}>{children}</PharmacistOrdersContext.Provider>;
}

export const usePharmacistOrders = () => {
  const {
    activeView,
    page,
    pageSize,
    orders,
    count,
    selectedOrders,
    loading,
    search,
    batchOrders,
    batchCount,
    refetchOrders,
    statusFilter,
    setSelectedOrders,
    setSearch,
    setActiveView,
    handlePaginationModelChange,
    handleSortingOrderChange,
    setProcessingDispense,
    setStatusFilter,
    shouldResetPageIndex,
    setShouldResetPageIndex,
    batchView,
    setBatchView,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = useContext(PharmacistOrdersContext);

  const changeTableView = (view: PharmacistOrderTableView) => {
    setSearch('');
    handlePaginationModelChange({ page: 0, pageSize });
    setActiveView(view);
    setSelectedOrders([]);
    // TODO: This doesn't actually reset the sortOrder
    handleSortingOrderChange([]);
    setStatusFilter([]);
    setShouldResetPageIndex(true);
    setStartDate(null);
    setEndDate(null);
  };

  const clearAllFilters = () => {
    changeTableView(activeView);
  };

  const toggleOrderSelection = (orderId: number) => {
    if (selectedOrders.includes(orderId)) {
      setSelectedOrders(selectedOrders.filter((id) => id !== orderId));
    } else {
      setSelectedOrders([...selectedOrders, orderId]);
    }
  };

  const areAnyOrdersSelected = () => {
    const ordersInPage = orders.map(({ id }) => id);
    const overlappingOrders = selectedOrders.filter((selectedId) => ordersInPage.includes(selectedId));
    return overlappingOrders.length;
  };

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const hasFiltersSelected = Boolean(startDate || endDate || search || statusFilter.length || areAnyOrdersSelected());

  // toggle behaviour:
  // if any orders are checked in the current page, deselects all orders in page
  // else selects all orders in page
  const toggleSelectAllOrders = () => {
    const ordersInPage = orders.map(({ id }) => id);
    if (areAnyOrdersSelected()) {
      setSelectedOrders(selectedOrders.filter((selectedId) => !ordersInPage.includes(selectedId)));
    } else {
      setSelectedOrders([...selectedOrders, ...ordersInPage]);
    }
  };

  const setFilter = (filter: string) => {
    setSearch(filter || '');
    setSelectedOrders([]);
  };

  const dispenseOrders = async (): Promise<ProcessDispenseResult> => {
    setProcessingDispense(true);
    const { data } = await processDispensing(selectedOrders);
    await refetchOrders();
    setProcessingDispense(false);
    setSelectedOrders([]);
    return data;
  };

  return {
    activeView,
    page,
    pageSize,
    orders,
    count,
    batchCount,
    selectedOrders,
    search,
    statusFilter,
    loading,
    setFilter,
    changeTableView,
    clearAllFilters,
    hasFiltersSelected,
    setSearch,
    toggleOrderSelection,
    areAnyOrdersSelected,
    setSelectedOrders,
    toggleSelectAllOrders,
    handlePaginationModelChange,
    handleSortingOrderChange,
    dispenseOrders,
    setStatusFilter,
    shouldResetPageIndex,
    batchOrders,
    batchView,
    setBatchView,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  };
};
